import {assertType, uuid} from "../base/utils";
import { Context } from "../base";
import { DiffContent, Entity, Mutation, TrackingBuilder } from "../base";
import { CachingRef, Ref } from "./Ref";

class Account implements Entity<Account> {
    static STORE = "Account";
    private _name : string;
    private _parent : Ref<Account> | undefined;
    private _uuid: string;

    constructor (uuid: string, name:string, parent?: Ref<Account>) {
        assertType(uuid, "uuid", String);
        assertType(name, "name", String);
        if (parent) {
            assertType(parent, "parent", Ref);
        }

        this._name = name;
        this._uuid = uuid;
        this._parent = parent;
    }

    fullName():string {
        if (this._parent instanceof Ref) {
            return this._parent.obj().fullName() + ":" + this._name;
        }
        return this._name;
    }

    getParents(): Ref<Account>[] {
        if (this._parent instanceof Ref) {
            return [this._parent].concat(this._parent.obj().getParents());
        }
        return [];
    }

    get uuid () { return this._uuid; }
    get name () { return this._name; }
    get parent() { return this._parent; }
    get fname() { return this.fullName(); }
    
    getRef() {
        return CachingRef.of(Account, this._uuid, this);
    }
    
    hasParent() {
        return this._parent !== undefined;
    }

    accountLevel():number {
        if (!this.parent) {
            return 0;
        }
        return this.parent.obj().accountLevel() + 1;
    }

    builder() : AccountBuilder {
        return new AccountBuilder()
            .trackingOff()
            .uuid(this._uuid)
            .name(this._name)
            .trackingOn();
    }

    toJson() : AccountJSON {
        return {
            uuid: this._uuid,
            name: this._name,
            parent: this._parent ? this._parent.obj().uuid : undefined
        };
    }

    static unit = new Account("AccountIdentity", "");
    static identity() {
        return Account.unit;
    }

    static createHierarchyTree(accounts:Account[]) : [Record<string,[Account,Account[]]>, Account[]] {
        const tree:Record<string,[Account,Account[]]> = {};
        
        accounts.forEach(acc => {
            tree[acc.uuid] = [acc, []]
        });
        const roots:Account[] = [];
        Object.values(tree)
        .forEach(([obj, _]) => {
            if (obj.hasParent() && obj.parent) {
                const parentUuid = obj.parent.uuid;
                const [_, children] = tree[parentUuid];
                children.push(obj);
            } else {
                roots.push(obj);
            }
        });
        return [tree, roots];
    }

    static getChildrenRefs(accounts:Account[], account:Account) {
        const [tree, _] = Account.createHierarchyTree(accounts);

        let explore = [account.uuid];
        let allChildren:string[] = []; 
        while(explore.length > 0) {
            const accountUuid = explore.pop();
            if (!accountUuid) {
                break;
            }
            const [_, children] = tree[accountUuid];
            if (children.length > 0) {
                const childrenIds = children.map(a => a.uuid);
                explore = explore.concat(childrenIds); 
                allChildren = allChildren.concat(childrenIds);
            }
        }
        return allChildren;
    }

    static fromName(fullname:string, accounts:Record<string, Account>, newAccountListener:(acc:Account) => void) : Account {
        if (fullname in accounts) {
            return accounts[fullname];
        }

        const parts = fullname.split(":");
        if (parts.length == 1) {
            const rootAccount = new Account(uuid(), parts[0]);
            newAccountListener(rootAccount);
            return rootAccount;
        }

        const parentName = parts.splice(0, parts.length - 1).join(":");
        const parentAccount = Account.fromName(parentName, accounts, newAccountListener);
        const account = new Account(uuid(), parts[0], parentAccount.getRef());
        newAccountListener(account);
        return account;
    }

    static fromUuid(uuid: string) : Account | undefined {
        const finder = Context.getInstance().getFinder(Account);
        return finder.findById(uuid);
    }

    static createRef(uuid: string) : Ref<Account> {
        return CachingRef.of(Account, uuid);
    }

    static fromJson(account:AccountJSON) {
        const id = (account.uuid) ? account.uuid : uuid();
        let parentRef : Ref<Account> | undefined = undefined;
        if (account.parent) {
            const parentAcc = Account.fromUuid(account.parent);
            if (!parentAcc) {
                throw new Error(`Invalid account uuid : ${account.parent}`);
            }
            parentRef = new Ref(Account, account.parent);
        }
        return new Account(id, account.name, parentRef);
    }

    static sort(accounts:Account[]) {
        assertType(accounts, "accounts", Array, Account);
        return accounts.sort((a, b) => a.fname.localeCompare(b.fname))
    }
}

class AccountBuilder extends TrackingBuilder<Account> {
    private _uuid: string;
    private _name: string;
    private _parent?: Ref<Account>;
    protected _diff:AccountDiff = {};
    
    constructor() {
        super();
        const cont = Account.identity();
        this._uuid = cont.uuid;
        this._name = cont.name;
        this._parent = cont.parent;
    }

    uuid(uuid:string) {
        if (this.isTracking() && this._uuid !== uuid) {
            this._diff.uuid = uuid;
        }
        this._uuid = uuid;
        return this;
    }

    name(name: string ) {
        if (this.isTracking() && this._name !== name) {
            this._diff.name = name;
        }
        this._name = name;
        return this;
    }

    parent(parent: Ref<Account> | string) {
        let parentAcc:Ref<Account> | undefined = undefined;
        if (typeof parent === "string") {
            const finder = Context.getInstance().getFinder(Account);
            const value = finder.findById(parent);
            if (!value) {
                console.log(`Invalid account uuid : ${parent}`)
                throw new Error(`Invalid account uuid : ${parent}`);
            }
            parentAcc = new Ref(Account, parent);
        } else {
            parentAcc = parent;
        }

        if ((this.isTracking() && !this._parent && parentAcc)
            || (this._parent && this._parent.uuid !== parentAcc.uuid)  ) {
            this._diff.parent = parentAcc.uuid;
        }
        this._parent = parentAcc;
        return this;
    }

    build(): Account {
        return new Account(this._uuid, this._name, this._parent);
    }
    
    applyDiff(diff: AccountDiff): AccountBuilder {
        if (diff.name){
            this.name(diff.name);
        }
        if (diff.uuid) {
            this.uuid(diff.uuid);
        }
        if (diff.parent) {
            this.parent(diff.parent);
        }
        return this;
    }

    applyObj(object:Account) : AccountBuilder {
        this.uuid(object.uuid);
        this.name(object.name);
        if (object.parent){
            this.parent(object.parent);
        }
        return this;
    }

    getDiff(): AccountDiff {
        return this._diff;
    }

}

interface AccountJSON {
    uuid: string,
    name: string,
    parent?: string,
}

interface AccountDiff extends DiffContent<Account> {
    uuid?: string,
    name?: string,
    parent?: Ref<Account> | string
}

export {Account, AccountJSON, AccountDiff, AccountBuilder};